<script setup lang="ts">
// 支付方式品牌图标 — 官方 SVG 来自 simple-icons (npm 分发的 git 图标库)
// 银联官方 SVG 已被 simple-icons 新版下架, 按其真实三色平行四边形徽标手绘复刻
import { siAlipay, siWechat, siTether } from 'simple-icons'

const props = defineProps<{ tone: string }>()

const brandPaths: Record<string, string> = {
  alipay: siAlipay.path,
  wechat: siWechat.path,
  usdt: siTether.path
}

const brandColor: Record<string, string> = {
  alipay: '#1677FF',
  wechat: '#07C160',
  union: '#E21836',
  quick: '#0891B2',
  usdt: '#26A17B',
  code: '#6D28D9'
}
</script>

<template>
  <span class="pay-mark" :style="{ '--pm-color': brandColor[props.tone] || '#64748B' }">
    <!-- 品牌单色 SVG (24×24 视区, fill currentColor 继承品牌色) -->
    <svg
      v-if="brandPaths[props.tone]"
      class="pm-svg" viewBox="0 0 24 24" aria-hidden="true"
    >
      <path :d="brandPaths[props.tone]" fill="currentColor"/>
    </svg>

    <!-- 银联: 红/蓝/绿三色平行四边形 (手绘复刻官方徽标) -->
    <svg v-else-if="props.tone === 'union'" class="pm-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.2 4.5h4.9c.85 0 1.38.68 1.2 1.52L8.9 17.98c-.18.84-1.03 1.52-1.88 1.52H2.12c-.85 0-1.38-.68-1.2-1.52L3.32 6.02C3.5 5.18 4.35 4.5 5.2 4.5Z" fill="#E21836" transform="translate(1.6 0)"/>
      <path d="M5.2 4.5h4.9c.85 0 1.38.68 1.2 1.52L8.9 17.98c-.18.84-1.03 1.52-1.88 1.52H2.12c-.85 0-1.38-.68-1.2-1.52L3.32 6.02C3.5 5.18 4.35 4.5 5.2 4.5Z" fill="#00447C" transform="translate(6.4 0)"/>
      <path d="M5.2 4.5h4.9c.85 0 1.38.68 1.2 1.52L8.9 17.98c-.18.84-1.03 1.52-1.88 1.52H2.12c-.85 0-1.38-.68-1.2-1.52L3.32 6.02C3.5 5.18 4.35 4.5 5.2 4.5Z" fill="#007B84" transform="translate(11.2 0)"/>
    </svg>

    <!-- 快捷支付: 银行卡 -->
    <svg
      v-else-if="props.tone === 'quick'"
      class="pm-svg pm-line" viewBox="0 0 24 24" aria-hidden="true"
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2.5"/>
      <path d="M2.5 9.5h19"/>
      <path d="M6 15h4"/>
    </svg>

    <!-- 个人固码: 收款二维码 -->
    <svg
      v-else-if="props.tone === 'code'"
      class="pm-svg pm-line" viewBox="0 0 24 24" aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5"/>
      <rect x="14" y="3" width="7" height="7" rx="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5"/>
      <path d="M14 14h3v3h-3zM21 14v.01M14 21h.01M17.5 17.5 21 21"/>
    </svg>

    <!-- 兜底: 首字母圆牌 -->
    <span v-else class="pm-fallback">¥</span>
  </span>
</template>

<style scoped>
.pay-mark {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  color: var(--pm-color);
  background: color-mix(in srgb, var(--pm-color) 10%, #fff);
  border: 1px solid color-mix(in srgb, var(--pm-color) 22%, transparent);
  transition: background 0.25s ease, transform 0.3s var(--ease-out-expo), box-shadow 0.3s ease;
}

/* 去周期性高光扫过 (廉价 glossy 痕迹): 保留 hover 交互, 常态静态 */
.pay-mark::after {
  content: none;
}

@media (prefers-reduced-motion: no-preference) {
  /* hover 时图标轻弹 + 轻微放大 */
  .pay-card:hover .pay-mark {
    animation: pm-bounce 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
}

@keyframes pm-shine {
  0%   { left: -60%; }
  22%  { left: 130%; }
  100% { left: 130%; }
}

@keyframes pm-glow {
  0%, 100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--pm-color) 0%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--pm-color) 14%, transparent),
      0 4px 14px -2px color-mix(in srgb, var(--pm-color) 22%, transparent);
  }
}

@keyframes pm-bounce {
  0%   { transform: scale(1) translateY(0); }
  45%  { transform: scale(1.12) translateY(-2px); }
  70%  { transform: scale(0.96) translateY(0); }
  100% { transform: scale(1.04) translateY(0); }
}

.pm-svg {
  width: 24px;
  height: 24px;
  display: block;
  transition: filter 0.3s ease;
}

/* hover 时 SVG 加强对比度与微弱发光 */
.pay-card:hover .pm-svg {
  filter: drop-shadow(0 2px 6px color-mix(in srgb, var(--pm-color) 36%, transparent));
}

/* 线性图标: 无 fill, 用描边 */
.pm-line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pm-fallback {
  font-weight: 800;
  font-size: 16px;
}

/* 卡片 hover 时图标微放大 + 底色加深 */
.pay-card:hover .pay-mark {
  background: color-mix(in srgb, var(--pm-color) 18%, #fff);
  transform: scale(1.08);
  box-shadow:
    0 4px 14px color-mix(in srgb, var(--pm-color) 30%, transparent),
    0 0 0 2px color-mix(in srgb, var(--pm-color) 16%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .pay-mark,
  .pay-mark::after,
  .pay-card:hover .pay-mark { animation: none !important; }
}
</style>
